import { keyboardVerticalOffset } from '@utils'
import React, { ReactNode } from 'react'
import { KeyboardAvoidingView, KeyboardAvoidingViewProps, Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

export const KeyboardAwareView = ({
  children,
  isFullScreen = false,
  hasOffset = false,
  offsetTopInset = false,
  behavior,
}: {
  children: ReactNode
  isFullScreen?: boolean
  hasOffset?: boolean
  // For full-screen screens whose content sits below the status-bar safe area
  // (i.e. inside `<Screen>`, which pads the top by `insets.top`). On iOS,
  // `behavior="padding"` measures the view frame *relative to its parent*, so
  // it under-pads by exactly the top inset and the input ends up partially
  // hidden behind the keyboard. Feeding `insets.top` as keyboardVerticalOffset
  // compensates that offset. iOS-only effect — on Android the behaviour is a
  // passthrough (adjustResize), where keyboardVerticalOffset is ignored.
  offsetTopInset?: boolean
  behavior?: KeyboardAvoidingViewProps['behavior']
}) => {
  const insets = useSafeAreaInsets()
  const verticalOffset = offsetTopInset
    ? insets.top
    : hasOffset
      ? keyboardVerticalOffset
      : undefined

  return (
    <KeyboardAvoidingView
      style={isFullScreen && { flex: 1, height: '100%' }}
      // Android: AndroidManifest declares `adjustResize` and MainActivity
      // no longer enables immersive sticky, so the system shrinks the
      // window when the keyboard opens. KAV must stay a passthrough or
      // it double-adjusts on top of `adjustResize`.
      behavior={behavior ?? (Platform.OS === 'ios' ? 'padding' : undefined)}
      keyboardVerticalOffset={verticalOffset}
    >
      {children}
    </KeyboardAvoidingView>
  )
}
